import { axiosInstance, create, get } from "./apiService";

/**
 * Submit a KYB (Know Your Business) application.
 *
 * Maps the frontend form data to the API's expected payload format:
 *   POST /api/v2/business/account-requests
 *
 * @param {object} formData - The full KYB form data from the flow
 * @returns {Promise<object>} The created BusinessAccountRequest
 */
export const submitKybApplication = async (formData) => {
  const trimmedRegistrationNumber = formData.registrationNumber?.trim();
  const payload = {
    businessName: formData.businessName,
    businessType: formData.businessType || undefined,
    ...(trimmedRegistrationNumber
      ? { registrationNumber: trimmedRegistrationNumber }
      : {}),
    contactPhone: formData.contactPhone || undefined,
    requesterNotes: buildRequesterNotes(formData),
    directorInvites: (formData.directors || []).map((d) => ({
      email: d.email.trim().toLowerCase(),
      fullName: d.fullName.trim(),
    })),
  };

  const response = await create("/api/v2/business/account-requests", payload);
  return response.data;
};

/**
 * Get the current user's KYB application requests.
 *
 *   GET /api/v2/business/account-requests/me
 *
 * @returns {Promise<object>} The user's requests
 */
export const getMyKybRequests = async () => {
  const response = await get("/api/v2/business/account-requests/me");
  return response.data;
};

/**
 * Withdraw the current user's open KYB application.
 *
 *   POST /api/v2/business/account-requests/me/withdraw
 *
 * @returns {Promise<object>} The withdrawn request
 */
export const withdrawKybApplication = async () => {
  const response = await create(
    "/api/v2/business/account-requests/me/withdraw",
    {}
  );
  return response.data;
};

export const getDirectorInviteByToken = async (token) => {
  const response = await get(
    `/api/v2/business/account-requests/director-invite/${encodeURIComponent(
      token
    )}`
  );
  return response.data;
};

export const linkDirectorInvite = async (token) => {
  const response = await create(
    "/api/v2/business/account-requests/director-invite/link",
    { token }
  );
  return response.data;
};

export const respondToDirectorInvite = async ({ requestId, action }) => {
  const response = await axiosInstance.patch(
    `/api/v2/business/account-requests/${encodeURIComponent(
      requestId
    )}/director-response`,
    { action }
  );
  return response.data;
};

/**
 * Build requester notes from extra form fields that the API
 * doesn't have dedicated columns for. This preserves the data
 * so admins can see it during review.
 */
function buildRequesterNotes(formData) {
  const parts = [];
  if (formData.tradingName) parts.push(`Trading Name: ${formData.tradingName}`);
  if (formData.businessType)
    parts.push(`Business Type: ${formData.businessType}`);
  if (formData.taxNumber) parts.push(`Tax Number: ${formData.taxNumber}`);
  if (formData.industrySector)
    parts.push(`Industry / Sector: ${formData.industrySector}`);
  if (formData.websiteUrl) parts.push(`Website: ${formData.websiteUrl}`);
  if (formData.businessAddress)
    parts.push(`Address: ${formData.businessAddress}`);
  return parts.length > 0 ? parts.join(" | ") : undefined;
}
